import {
  PERMISSION_ENTITY_INVOICE,
  PERMISSION_ENTITY_INVOICE_OFFER,
  PERMISSION_ENTITY_OPERATIONAL,
  PERMISSION_ENTITY_PURCHASE_ORDER
} from '@klear/constants'
import { formatBasisPoints } from '@klear/basis-points/boundary'
import type { DateDisplayInput } from '@klear/order-event/list-display'
import { dateFormatted, domainListItemEffectiveDate, domainListItemNumber } from '@klear/order-event/list-display'
import type { OrderEventTimelineItem } from '@klear/order-event/order-event-timeline-tree'
import { timelineItemId, timelineItemParentId } from '@klear/order-event/order-event-timeline-tree'
import isString from 'lodash/isString'

export type OrderEventFilePreviewBreadcrumbItem = {
  id: number
  idDisplay: string
  typeLabel: string
  number?: string
}

export type OrderEventFilePreviewOrderSummary = {
  idDisplay: string
  displayName: string
}

export type OrderEventFilePreviewDetails = {
  typeLabel: string
  effectiveDate: string | null
  endDate?: string | null
  number?: string
  amountFormatted?: string | null
  notes?: string | null
  advanceRateFormatted?: string | null
  feeFormatted?: string | null
  settlementCollectionFormatted?: string | null
  settlementAdvanceRecoveryFormatted?: string | null
  settlementNetAmountFormatted?: string | null
}

export type OrderEventFilePreviewContext = {
  traderId: number
  eventId: number
  eventType: string
  idDisplay: string
  order: OrderEventFilePreviewOrderSummary
  ancestors: OrderEventFilePreviewBreadcrumbItem[]
  details: OrderEventFilePreviewDetails
}

export function orderEventTypeLabel(type: string): string {
  return type
    .split('_')
    .map((part) => (part.length > 0 ? part.charAt(0).toUpperCase() + part.slice(1) : part))
    .join(' ')
}

function entryTypeLabel(item: OrderEventTimelineItem): string {
  if (item.type === PERMISSION_ENTITY_OPERATIONAL) {
    const title = item.model.title
    if (isString(title) && title.trim() !== '') return title
  }
  return orderEventTypeLabel(item.type)
}

function entryNotes(item: OrderEventTimelineItem): string | null {
  const notes = (item.model as { notes?: unknown }).notes
  return isString(notes) ? notes : null
}

function entryEndDate(item: OrderEventTimelineItem): string | null {
  return dateFormatted((item.model as { endDate?: DateDisplayInput }).endDate)
}

export function orderEventAncestorChain(
  eventId: number,
  flatItems: readonly OrderEventTimelineItem[]
): OrderEventFilePreviewBreadcrumbItem[] {
  const byId = new Map(flatItems.map((item) => [timelineItemId(item), item]))
  const chain: OrderEventFilePreviewBreadcrumbItem[] = []
  const current = byId.get(eventId)
  if (current != null) {
    let parentId: number | undefined = timelineItemParentId(current)
    while (parentId != null) {
      const parent = byId.get(parentId)
      if (parent == null) break
      chain.unshift({
        id: timelineItemId(parent),
        idDisplay: parent.envelope.idDisplay,
        typeLabel: entryTypeLabel(parent),
        number: domainListItemNumber(parent)
      })
      parentId = timelineItemParentId(parent)
    }
  }
  return chain
}

export function orderEventFilePreviewDetails(item: OrderEventTimelineItem): OrderEventFilePreviewDetails {
  const base: OrderEventFilePreviewDetails = {
    typeLabel: entryTypeLabel(item),
    effectiveDate: dateFormatted(domainListItemEffectiveDate(item)),
    endDate: entryEndDate(item),
    number: domainListItemNumber(item),
    notes: entryNotes(item)
  }

  switch (item.type) {
    case PERMISSION_ENTITY_PURCHASE_ORDER:
      return { ...base, amountFormatted: item.model.amount.toString() }
    case PERMISSION_ENTITY_INVOICE:
      return { ...base, amountFormatted: item.model.amount.toString() }
    case PERMISSION_ENTITY_OPERATIONAL:
      return {
        ...base,
        amountFormatted: item.model.amount.isPositive() ? item.model.amount.toString() : null
      }
    case PERMISSION_ENTITY_INVOICE_OFFER: {
      const offer = item.model
      return {
        ...base,
        amountFormatted: offer.purchasePrice.toString(),
        advanceRateFormatted: formatBasisPoints(offer.advanceRate, { digits: 1 }),
        settlementCollectionFormatted: offer.settlementCollection.toString(),
        settlementAdvanceRecoveryFormatted: offer.settlementAdvanceRecovery.toString(),
        settlementNetAmountFormatted: offer.settlementNetAmount.toString()
      }
    }
    default:
      return base
  }
}

export function orderEventFilePreviewContext(
  item: OrderEventTimelineItem,
  flatItems: readonly OrderEventTimelineItem[],
  traderId: number,
  order: OrderEventFilePreviewOrderSummary
): OrderEventFilePreviewContext {
  const eventId = timelineItemId(item)
  return {
    traderId,
    eventId,
    eventType: item.type,
    idDisplay: item.envelope.idDisplay,
    order,
    ancestors: orderEventAncestorChain(eventId, flatItems),
    details: orderEventFilePreviewDetails(item)
  }
}

export function isInvoiceOfferFilePreviewContext(ctx: OrderEventFilePreviewContext): boolean {
  return ctx.eventType === PERMISSION_ENTITY_INVOICE_OFFER
}
